"use client";

import type { ComponentPropsWithoutRef } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { ApolloFormProvider, use_apollo_form_provider_state } from "./ApolloFormProvider";
import {
	APOLLO_FIELD_LIBRARY,
	type ApolloFieldConfig,
	type ApolloFieldName,
} from "./apollo-field-library";

interface ApolloMatchedAction {
	action_type?: string;
	value?: string;
	header?: string;
}

interface ApolloSubmitPayload {
	matched_action?: ApolloMatchedAction;
	matchedAction?: ApolloMatchedAction;
	data?: {
		matched_action?: ApolloMatchedAction;
		matchedAction?: ApolloMatchedAction;
	};
	result?: {
		matched_action?: ApolloMatchedAction;
		matchedAction?: ApolloMatchedAction;
	};
}

interface ApolloSubmitErrorPayload {
	mandatory_questions?: unknown;
}

const APOLLO_ALLOWED_HTML_TAGS = [
	"a",
	"b",
	"br",
	"div",
	"em",
	"i",
	"li",
	"ol",
	"p",
	"span",
	"strong",
	"u",
	"ul",
] as const;

const APOLLO_ALLOWED_URI_REGEXP = /^(https?:|mailto:)/i;

const sanitize_apollo_message_html = (raw_html: string): string => {
	if (typeof window === "undefined") return raw_html;

	const sanitized_html = DOMPurify.sanitize(raw_html, {
		ALLOWED_TAGS: [...APOLLO_ALLOWED_HTML_TAGS],
		ALLOWED_ATTR: ["href", "rel", "target"],
		ALLOW_UNKNOWN_PROTOCOLS: false,
		ALLOWED_URI_REGEXP: APOLLO_ALLOWED_URI_REGEXP,
	});

	const parser = new DOMParser();
	const parsed_document = parser.parseFromString(sanitized_html, "text/html");
	const anchor_nodes = Array.from(parsed_document.body.querySelectorAll("a"));
	for (const anchor_node of anchor_nodes) {
		const href = anchor_node.getAttribute("href") ?? "";
		if (!APOLLO_ALLOWED_URI_REGEXP.test(href)) {
			anchor_node.removeAttribute("href");
		}
		anchor_node.setAttribute("rel", "noopener noreferrer");
	}

	return parsed_document.body.innerHTML;
};

interface ApolloMeetingFormProps {
	scheduling_link: string;
	fields: readonly ApolloFieldName[];
	required_fields?: readonly ApolloFieldName[];
	field_labels?: Partial<Record<ApolloFieldName, string>>;
	app_id?: string;
	form_id?: string;
	hidden_form_id_value?: string;
	widget_options?: Record<string, unknown>;
	success_message?: string;
	title?: string;
	description?: string;
	on_submit_success?: () => void;
}

const FORM_CLASS_NAME = "not-prose w-full max-w-lg";
const CONTAINER_CLASS_NAME = "relative w-full rounded-md bg-inherit px-4 py-4 text-left sm:px-5 sm:py-5";
const TITLE_CLASS_NAME = "text-left font-lora text-[24px] font-bold leading-tight tracking-tight text-black sm:text-[26px]";
const DESCRIPTION_CLASS_NAME = "mt-3 text-left text-base font-sans leading-tight text-black";
const FORM_GRID_CLASS_NAME = "mt-5 grid grid-cols-1 gap-5";
const FIELD_CONTAINER_CLASS_NAME = "space-y-2";
const LABEL_CLASS_NAME = "block text-left text-base font-medium leading-none text-black";
const INPUT_CLASS_NAME = "h-9 w-full rounded-sm border border-[#D1D5DB] bg-white px-3 text-sm text-black outline-hidden transition focus:border-ss-blue focus:ring-2 focus:ring-ss-blue/20";
const TEXTAREA_CLASS_NAME = "min-h-[104px] w-full rounded-sm border border-[#D1D5DB] bg-white px-3 py-2 text-sm text-black outline-hidden transition focus:border-ss-blue focus:ring-2 focus:ring-ss-blue/20";
const RADIO_GROUP_CLASS_NAME = "rounded-sm border border-[#D1D5DB] bg-white px-3 py-2";
const SUBMIT_BUTTON_CLASS_NAME = "inline-flex h-11 w-full items-center justify-center rounded-sm bg-[#005198] px-5 text-base font-bold text-white transition hover:bg-[#004785] focus:outline-hidden focus:ring-2 focus:ring-[#005198] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#005198]";
const INVALID_FIELD_CLASS_NAME = "border-red-500 focus:border-red-500 focus:ring-red-200";

const set_apollo_widget_modal_visibility = (is_visible: boolean) => {
	if (typeof document === "undefined") return;
	const widget_modal_nodes = Array.from(
		document.querySelectorAll<HTMLElement>(
			"#apollo-meetings-widget, [id^='apollo-meetings-widget'], [id*='apollo-meetings-widget'], [class*='apollo-meetings-widget']"
		)
	).filter((node) => node.tagName.toLowerCase() !== "script");
	if (widget_modal_nodes.length === 0) return;

	if (!is_visible) {
		widget_modal_nodes.forEach((node) => {
			node.style.display = "none";
			node.style.pointerEvents = "none";
			node.setAttribute("aria-hidden", "true");
		});
		return;
	}

	const preferred_widget_node =
		[...widget_modal_nodes].reverse().find((node) => node.childElementCount > 0) ??
		widget_modal_nodes[widget_modal_nodes.length - 1];

	widget_modal_nodes.forEach((node) => {
		const is_preferred = node === preferred_widget_node;
		node.style.display = is_preferred ? "" : "none";
		node.style.pointerEvents = is_preferred ? "auto" : "none";
		node.setAttribute("aria-hidden", is_preferred ? "false" : "true");
	});
};

function ApolloSubmitButton() {
	const provider_state = use_apollo_form_provider_state();
	const fallback_is_ready = typeof window !== "undefined" && Boolean(window.ApolloMeetings);
	const is_ready = provider_state?.is_ready ?? fallback_is_ready;
	const is_loading = provider_state?.is_loading ?? !fallback_is_ready;

	return (
		<button
			type="submit"
			className={SUBMIT_BUTTON_CLASS_NAME}
			disabled={!is_ready}
			aria-busy={is_loading}
		>
			{is_loading ? "Loading..." : "Submit"}
		</button>
	);
}

export function ApolloForm({
	scheduling_link,
	fields,
	required_fields,
	field_labels,
	app_id,
	form_id,
	hidden_form_id_value,
	widget_options,
	success_message = "Form submitted.",
	title,
	description,
	on_submit_success,
}: ApolloMeetingFormProps) {
	const t = useTranslations("forms.apollo");
	const [show_success_message, set_show_success_message] = useState(false);
	const [success_header, set_success_header] = useState<string>("Success");
	const [success_html, set_success_html] = useState<string | null>(null);
	const [invalid_field_names, set_invalid_field_names] = useState<ApolloFieldName[]>([]);
	const [success_overlay_background_color, set_success_overlay_background_color] = useState<string>("rgba(255, 255, 255, 0.9)");
	const form_container_ref = useRef<HTMLDivElement | null>(null);
	const generated_form_id = useId();
	const resolved_form_id = useMemo(() => {
		if (form_id) return form_id;
		return `apollo-meeting-form-${generated_form_id.replace(/[^a-zA-Z0-9_-]/g, "")}`;
	}, [form_id, generated_form_id]);
	const resolved_hidden_form_id_value = hidden_form_id_value ?? resolved_form_id;
	const localized_field_labels: Partial<Record<ApolloFieldName, string>> = {
		organization: t("fields.organization"),
		org_name: t("fields.org_name"),
	};

	const unique_fields = useMemo(() => {
		return Array.from(new Set(fields));
	}, [fields]);
	const required_field_set = useMemo(() => {
		if (!required_fields) return null;
		return new Set(required_fields);
	}, [required_fields]);

	const parse_required_field_errors = (error: unknown): ApolloFieldName[] => {
		if (!error || typeof error !== "object") return [];
		const payload = error as ApolloSubmitErrorPayload;
		if (!Array.isArray(payload.mandatory_questions)) return [];

		return payload.mandatory_questions.filter((field_name): field_name is ApolloFieldName => {
			return typeof field_name === "string" && field_name in APOLLO_FIELD_LIBRARY;
		});
	};

	const handle_submit_success = (payload?: unknown) => {
		const submit_payload = (payload ?? {}) as ApolloSubmitPayload;
		const matched_action =
			submit_payload.matched_action ??
			submit_payload.matchedAction ??
			submit_payload.data?.matched_action ??
			submit_payload.data?.matchedAction ??
			submit_payload.result?.matched_action ??
			submit_payload.result?.matchedAction;
		const is_custom_message = matched_action?.action_type === "custom_message";

		set_success_header(matched_action?.header || "Form submitted");
		if (is_custom_message) {
			set_success_html(matched_action?.value ? sanitize_apollo_message_html(matched_action.value) : null);
			set_apollo_widget_modal_visibility(false);
		} else {
			set_success_html(null);
			set_apollo_widget_modal_visibility(true);
		}
		set_show_success_message(true);

		set_invalid_field_names([]);
		on_submit_success?.();
	};

	const handle_submit_error = (error: unknown) => {
		set_show_success_message(false);
		set_success_html(null);
		set_apollo_widget_modal_visibility(true);
		set_invalid_field_names(parse_required_field_errors(error));
	};

	useEffect(() => {
		if (!show_success_message) return;
		const resolve_background_color = (): string => {
			let current: HTMLElement | null = form_container_ref.current;
			while (current) {
				const background_color = window.getComputedStyle(current).backgroundColor;
				if (background_color && background_color !== "transparent" && background_color !== "rgba(0, 0, 0, 0)") {
					return background_color;
				}
				current = current.parentElement;
			}
			return "rgb(255, 255, 255)";
		};

		const to_alpha = (color: string, alpha: number): string => {
			const rgba_match = color.match(/^rgba?\(([^)]+)\)$/i);
			if (!rgba_match) return `rgba(255, 255, 255, ${alpha})`;
			const channels = rgba_match[1].split(",").map((value) => value.trim());
			if (channels.length < 3) return `rgba(255, 255, 255, ${alpha})`;
			return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`;
		};

		set_success_overlay_background_color(to_alpha(resolve_background_color(), 0.9));
	}, [show_success_message]);

	return (
		<ApolloFormProvider
			app_id={app_id}
			className={FORM_CLASS_NAME}
			form_id={resolved_form_id}
			on_submit_error={handle_submit_error}
			on_submit_success={handle_submit_success}
			scheduling_link={scheduling_link}
			show_submit_button={false}
			widget_options={widget_options}
		>
			<div ref={form_container_ref} className={CONTAINER_CLASS_NAME}>
				{show_success_message && (
					<div className="absolute inset-0 z-20 rounded-md p-6 text-center">
						<div
							className="absolute inset-0 rounded-md backdrop-blur-sm"
							style={{ backgroundColor: success_overlay_background_color }}
						/>
						<div className="relative z-10 flex h-full items-center justify-center">
							<div className="mx-auto max-w-2xl text-center">
								<h4 className="text-xl font-semibold text-gray-900">
									{success_header}
								</h4>
							{success_html ? (
									<div
										className="mt-3 text-base leading-relaxed [&_a]:font-semibold [&_a]:text-[#005198] [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-2 [&_a]:transition-colors [&_a:hover]:text-[#003f76] [&_a:focus-visible]:outline-hidden [&_a:focus-visible]:ring-2 [&_a:focus-visible]:ring-[#005198]/40 [&_a:focus-visible]:rounded-sm"
										dangerouslySetInnerHTML={{ __html: success_html }}
									/>
								) : (
									<p className="mt-3 text-base font-medium text-gray-900">
										{success_message}
									</p>
								)}
							</div>
						</div>
					</div>
				)}
				{title && (
					<h3 className={TITLE_CLASS_NAME}>
						{title}
					</h3>
				)}
				{description && (
					<p className={DESCRIPTION_CLASS_NAME}>
						{description}
					</p>
				)}
				<input
					type="hidden"
					name="form_id"
					data-apollo-map-id="form_id"
					value={resolved_hidden_form_id_value}
					readOnly
				/>

				<div className={FORM_GRID_CLASS_NAME}>
					{unique_fields.map((field_name) => {
						const base_config = APOLLO_FIELD_LIBRARY[field_name];
						if (!base_config) return null;
						const merged_config: ApolloFieldConfig = base_config;

							const input_id = `${resolved_form_id}-${field_name}`;
							const is_textarea = merged_config.input_type === "textarea";
							const is_radio_group = merged_config.input_type === "radio_group";
							const is_required = required_field_set
								? required_field_set.has(field_name)
								: (merged_config.required ?? false);
							const is_invalid = invalid_field_names.includes(field_name);
							const resolved_field_label = field_labels?.[field_name] ?? localized_field_labels[field_name] ?? merged_config.label;
							const resolved_input_class_name = is_invalid
								? `${INPUT_CLASS_NAME} ${INVALID_FIELD_CLASS_NAME}`
								: INPUT_CLASS_NAME;
							const resolved_textarea_class_name = is_invalid
								? `${TEXTAREA_CLASS_NAME} ${INVALID_FIELD_CLASS_NAME}`
								: TEXTAREA_CLASS_NAME;
							const resolved_radio_group_class_name = is_invalid
								? `${RADIO_GROUP_CLASS_NAME} ${INVALID_FIELD_CLASS_NAME}`
								: RADIO_GROUP_CLASS_NAME;
							const handle_field_change = () => {
								if (!is_invalid) return;
								set_invalid_field_names((current) => current.filter((value) => value !== field_name));
							};

							return (
								<div key={field_name} className={FIELD_CONTAINER_CLASS_NAME}>
								{!is_radio_group && (
									<label htmlFor={input_id} className={LABEL_CLASS_NAME}>
										{resolved_field_label}
										{is_required && (
											<span aria-hidden="true"> *</span>
										)}
									</label>
								)}
								{is_textarea ? (
									<textarea
										id={input_id}
										name={field_name}
										data-apollo-map-id={field_name}
										required={is_required}
										aria-invalid={is_invalid}
										placeholder={merged_config.placeholder}
										className={resolved_textarea_class_name}
										onChange={handle_field_change}
									/>
								) : is_radio_group ? (
									<fieldset aria-invalid={is_invalid} className={resolved_radio_group_class_name}>
										<legend className={LABEL_CLASS_NAME}>
											{resolved_field_label}
											{is_required && (
												<span aria-hidden="true"> *</span>
											)}
										</legend>
										<div className="mt-2 flex flex-wrap gap-4">
											{(merged_config.options ?? []).map((option_value, option_index) => {
												const option_id = `${input_id}-${option_value}`;
												return (
													<label key={option_value} htmlFor={option_id} className="inline-flex items-center gap-2 text-sm text-black">
														<input
															id={option_id}
															name={field_name}
															data-apollo-map-id={field_name}
															type="radio"
															value={option_value}
															required={is_required && option_index === 0}
															onChange={handle_field_change}
														/>
														<span className="capitalize">{option_value}</span>
													</label>
												);
											})}
										</div>
									</fieldset>
								) : (
									<input
										id={input_id}
										name={field_name}
										data-apollo-map-id={field_name}
										type={merged_config.input_type as ComponentPropsWithoutRef<"input">["type"]}
										required={is_required}
										aria-invalid={is_invalid}
										autoComplete={merged_config.auto_complete}
										placeholder={merged_config.placeholder}
										className={resolved_input_class_name}
										onChange={handle_field_change}
									/>
								)}
							</div>
						);
					})}
				</div>

				<div className="mt-5 max-w-3xl text-left! text-[11px]! italic leading-[1.3]! text-black sm:text-[12px]!">
					By clicking submit, you consent to receive occasional marketing communications from StudyStash. You can unsubscribe at any time. For more information, please review our{" "}
					<Link href="/privacy-policy" className="text-[#005198] underline underline-offset-4">
						Privacy Policy
					</Link>.
				</div>

				<div className="mt-6">
					<ApolloSubmitButton />
				</div>

				<div className="mt-5 space-y-2 text-left">
					<div className="text-[10px]! leading-[1.3]! text-[#6B7280] sm:text-[11px]!">
						Required fields are marked with <span className="font-semibold">*</span>
					</div>
					<div className="inline-flex w-full items-start rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800">
						<div className="text-[10px]! leading-[1.3]! sm:text-[11px]!">
							Never share sensitive information (credit card numbers, social security numbers, passwords) through this form.
						</div>
					</div>
					<div className="text-[10px]! leading-[1.3]! text-[#6B7280] sm:text-[11px]!">
						This site is protected by reCAPTCHA and the Google{" "}
						<Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#005198] underline underline-offset-4">
							Privacy Policy
						</Link>{" "}
						and{" "}
						<Link href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#005198] underline underline-offset-4">
							Terms of Service
						</Link>{" "}
						apply.
					</div>
				</div>
			</div>
		</ApolloFormProvider>
	);
}
